import { ref } from "vue";
import type { TreeDefinitionDto, TreeNodeDto } from "~/api";
import { buildTreeNodeMap } from "~/composables/useGeoTree";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";

/**
 * Mirrors the server-side `SiteResultEligibilityResolver.LegacyUnitTypeToLeafName`
 * map. Used to resolve legacy `RoomPreference` / `eligibleUnits` codes to V2
 * UnitType leaf-node names.
 */
export const LEGACY_UNIT_TYPE_TO_LEAF_NAME: Record<string, string> = {
  s: "Studio",
  supports: "Studio",
  "1": "1 Bedroom",
  "2": "2 Bedroom",
  "3": "3 Bedroom",
  "4": "4 Bedroom",
};

// Module-scoped state so every useUnitTypeTree() consumer shares the same
// tree + lookup map. The tree is fetched once per org; subsequent callers
// hit the cache or piggyback on the in-flight request.
const unitTypeTreeData = ref<TreeDefinitionDto | null>(null);
const flatNodeMap = ref(new Map<string, TreeNodeDto>());
const loading = ref(false);
let loadedOrgGuid: string | null = null;
let inflightLoad: Promise<void> | null = null;

/**
 * Composable for loading the UnitType tree definition and building
 * a flat GUID → TreeNodeDto lookup map. Used by the finder UI to render
 * room-option labels from V2 leaf-node guids.
 */
export function useUnitTypeTree() {
  const myInfoStore = useMyInfoStore();

  const loadUnitTypeTree = async (): Promise<void> => {
    await myInfoStore.load();
    const orgGuid = myInfoStore.currentSysOrgGuid;

    if (loadedOrgGuid === orgGuid && unitTypeTreeData.value) return;
    if (inflightLoad) return inflightLoad;

    loading.value = true;
    inflightLoad = (async () => {
      try {
        const tree = await apiClient.tree.getTreeDefinitionByType(
          orgGuid ?? emptyGuid,
          "UnitType",
        );
        unitTypeTreeData.value = tree;
        flatNodeMap.value.clear();
        buildTreeNodeMap(tree.nodes, flatNodeMap.value);
        loadedOrgGuid = orgGuid;
      } catch {
        // No unit-type tree configured for this org
      } finally {
        loading.value = false;
        inflightLoad = null;
      }
    })();

    return inflightLoad;
  };

  const getNodeName = (guid: string): string | undefined =>
    flatNodeMap.value.get(guid)?.name;

  const getLeafGuidByName = (name: string): string | undefined => {
    for (const node of flatNodeMap.value.values()) {
      if (node.name === name) return node.guid;
    }
    return undefined;
  };

  /**
   * Maps a legacy room code ("s", "1", "2", "supports", ...) to its corresponding
   * UnitType leaf-node guid via the canonical name map. Returns undefined if the
   * code or leaf isn't known to this org's tree.
   */
  const getLeafGuidByLegacyCode = (code: string): string | undefined => {
    const leafName = LEGACY_UNIT_TYPE_TO_LEAF_NAME[code.toLowerCase()];
    if (!leafName) return undefined;
    return getLeafGuidByName(leafName);
  };

  return {
    unitTypeTreeData,
    flatNodeMap,
    loading,
    loadUnitTypeTree,
    getNodeName,
    getLeafGuidByName,
    getLeafGuidByLegacyCode,
  };
}
